import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Users,
  UserCog,
  FolderKanban,
  GitBranch,
  Settings,
  Bell,
  FileText,
  Activity,
  Bot,
  TrendingUp,
  Clock,
  DollarSign,
  KanbanSquare,
  KeyRound,
  Eye,
  Cpu,
  Wrench,
  Server,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/utils/helpers';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
}

const navigation: NavItem[] = [
  // Same rules as the API (backend access/scope.ts): employees see the board, projects, repos and AI sessions
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['executive', 'pm'] },
  { name: 'Overview', href: '/overview', icon: BarChart3, roles: ['executive', 'pm'] },
  { name: 'Board', href: '/board', icon: KanbanSquare, roles: ['executive', 'pm', 'developer', 'support'] },
  { name: 'Users', href: '/users', icon: Users, roles: ['executive', 'pm'] },
  { name: 'User Management', href: '/user-management', icon: UserCog, roles: ['executive'] },
  { name: 'Projects', href: '/projects', icon: FolderKanban, roles: ['executive', 'pm', 'developer', 'support'] },
  { name: 'Repositories', href: '/repos', icon: GitBranch, roles: ['executive', 'pm', 'developer', 'support'] },
  { name: 'Activity', href: '/activity', icon: Activity, roles: ['executive', 'pm'] },
  { name: 'AI Reviews', href: '/reviews', icon: Bot, roles: ['executive', 'pm'] },
  { name: 'Alerts', href: '/alerts', icon: Bell, roles: ['executive', 'pm'] },
  { name: 'Performance', href: '/performance', icon: TrendingUp, roles: ['executive', 'pm'] },
  { name: 'Reports', href: '/reports', icon: FileText, roles: ['executive', 'pm'] },
  { name: 'AI sessions', href: '/agents', icon: Cpu, roles: ['executive', 'pm', 'developer', 'support'] },
  { name: 'Project setup', href: '/project-setup', icon: Wrench, roles: ['executive', 'pm'] },
  { name: 'Servers', href: '/servers', icon: Server, roles: ['executive', 'pm'] },
  { name: 'Shadow review', href: '/shadow-review', icon: Eye, roles: ['executive'] },
  { name: 'Jobs', href: '/jobs', icon: Clock, roles: ['executive'] },
  { name: 'AI Costs', href: '/ai-costs', icon: DollarSign, roles: ['executive'] },
  { name: 'Access tokens', href: '/access-tokens', icon: KeyRound, roles: ['executive'] },
  { name: 'Settings', href: '/settings', icon: Settings, roles: ['executive', 'pm'] },
];

export function Sidebar() {
  const { user } = useAuth();

  const filteredNavigation = navigation.filter((item) => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  return (
    <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-card border-r">
      <div className="flex h-16 items-center border-b px-6">
        <h1 className="text-lg font-semibold">AI Manager</h1>
      </div>

      <nav className="flex flex-col gap-1 p-4">
        {filteredNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.name}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
